import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";
import {
  ClientSubscription,
  ClientSubscriptionPlan,
  CreateClientSubscriptionPlanPayload,
  UpdateClientSubscriptionPlanPayload,
} from "../types/clientSubscription";

const api = new APIClient();

const PLANS = "/client-subscription-plans";

export type {
  ClientSubscription,
  ClientSubscriptionPlan,
  ClubSummary,
  CreateClientSubscriptionPlanPayload,
  UpdateClientSubscriptionPlanPayload,
} from "../types/clientSubscription";

export interface ClientSubscriptionPlanListParams extends PaginationParams {
  search?: string;
  is_active?: boolean;
}

export const getClientSubscriptionPlans = (
  params?: ClientSubscriptionPlanListParams
): Promise<PaginatedApiResponse<ClientSubscriptionPlan>> =>
  api.get<PaginatedApiResponse<ClientSubscriptionPlan>>(PLANS, params);

export const createClientSubscriptionPlan = (
  payload: CreateClientSubscriptionPlanPayload
): Promise<ApiResponse<ClientSubscriptionPlan>> =>
  api.create<ApiResponse<ClientSubscriptionPlan>>(PLANS, payload);

export const updateClientSubscriptionPlan = (
  id: number,
  payload: UpdateClientSubscriptionPlanPayload
): Promise<ApiResponse<ClientSubscriptionPlan>> =>
  api.update<ApiResponse<ClientSubscriptionPlan>>(`${PLANS}/${id}`, payload);

export const deleteClientSubscriptionPlan = (
  id: number
): Promise<ApiResponse<ClientSubscriptionPlan>> =>
  api.delete<ApiResponse<ClientSubscriptionPlan>>(`${PLANS}/${id}`);

export const getClientSubscriptions = (
  clientId: number
): Promise<ApiResponse<ClientSubscription[]>> =>
  api.get<ApiResponse<ClientSubscription[]>>(
    `/clients/${clientId}/subscriptions`
  );

export const createClientSubscription = (
  clientId: number,
  payload: {
    client_subscription_plan_id: number;
    activate_manually?: boolean;
    payment_method?: string;
  }
): Promise<
  ApiResponse<{
    subscription: ClientSubscription;
    payment_link_url: string | null;
  }>
> =>
  api.create<
    ApiResponse<{
      subscription: ClientSubscription;
      payment_link_url: string | null;
    }>
  >(`/clients/${clientId}/subscriptions`, payload);

export interface ClubBenefitPreview {
  covered: boolean;
  limit_reached?: boolean;
  plan_name: string | null;
  subscription_id: number | null;
  visits_used: number | null;
  visits_limit: number | null;
}

export interface ClubMetrics {
  active_count: number;
  past_due_count: number;
  suspended_count: number;
  churn_this_month_count: number;
  mrr_cents: number;
  mrr_active_cents: number;
  mrr_past_due_cents: number;
  revenue_collected_this_month_cents: number;
  month_start: string;
  month_end: string;
}

export const getClientClubBenefitPreview = (
  clientId: number,
  serviceId: number
): Promise<ApiResponse<ClubBenefitPreview>> =>
  api.get(`/clients/${clientId}/club-benefit-preview`, {
    service_id: serviceId,
  });

export const getClientSubscriptionMetrics = (): Promise<
  ApiResponse<ClubMetrics>
> => api.get<ApiResponse<ClubMetrics>>("/client-subscriptions/metrics");

export const cancelClientSubscription = (
  clientId: number,
  subscriptionId: number,
  immediate = true
): Promise<ApiResponse<ClientSubscription>> =>
  api.create<ApiResponse<ClientSubscription>>(
    `/clients/${clientId}/subscriptions/${subscriptionId}/cancel`,
    { immediate }
  );

export const changeClientSubscriptionPlan = (
  clientId: number,
  subscriptionId: number,
  clientSubscriptionPlanId: number
): Promise<
  ApiResponse<{
    subscription: ClientSubscription;
    proration_cents: number;
    proration_invoice: unknown;
    payment_link_url: string | null;
  }>
> =>
  api.create(
    `/clients/${clientId}/subscriptions/${subscriptionId}/change-plan`,
    { client_subscription_plan_id: clientSubscriptionPlanId }
  );

export const getClientSubscriptionVisits = (
  clientId: number,
  subscriptionId: number
): Promise<
  ApiResponse<{
    subscription_id: number;
    period_start: string | null;
    period_end: string | null;
    visit_dates: string[];
    visits_used: number;
  }>
> =>
  api.get(
    `/clients/${clientId}/subscriptions/${subscriptionId}/visits`
  );

export const createClientClubShareLink = (
  clientId: number,
  planId?: number | null
): Promise<
  ApiResponse<{
    url: string;
    token: string;
    expires_at: number;
    qr_data_uri?: string | null;
  }>
> =>
  api.create(`/clients/${clientId}/club/share-link`, {
    client_subscription_plan_id: planId ?? undefined,
  });

export const inviteClientClubWhatsApp = (
  clientId: number,
  planId?: number | null
): Promise<
  ApiResponse<{
    mode: "template" | "free_text";
    url: string;
    token: string;
    expires_at: number;
    wa_url: string | null;
    template_queued: boolean;
    template_available: boolean;
    qr_data_uri?: string | null;
    message_preview?: string;
  }>
> =>
  api.create(`/clients/${clientId}/club/invite-whatsapp`, {
    client_subscription_plan_id: planId ?? undefined,
  });
